// PRD.317 — optional dental tooth context for a generic Diagnosis.
//
// Diagnosis stays canonical and multi-specialty. Teeth live in a narrow
// association (`patient_diagnosis_teeth`) keyed by the diagnosis row's stable
// UUID, so nothing here maps by text, label, index or render order.
//
// Tooth identity is the FDI string the Part Chart already uses as its
// `tooth_states` keys and PRD.316 stores on findings — this module reuses that
// representation rather than introducing a second one. No surfaces in V1
// (PRD §7): a diagnosis needs the affected tooth, not a restorative surface.
//
// Self-contained (no workspace-package imports) so it runs under node:test.

// ─── Rows ───

export interface DiagnosisToothRow {
  id: string;
  org_id: string | null;
  clinic_id: string | null;
  diagnosis_entry_id: string | null;
  patient_id: string | null;
  encounter_id: string | null;
  tooth: string | null;
  created_at: string | null;
  updated_at: string | null;
  updated_by: string | null;
}

// ─── Identity ───

/**
 * Deterministic row id for one (diagnosis, tooth) pair.
 *
 * Follows the `specialtyHistoryId` precedent: two devices adding tooth 16 to
 * the same diagnosis derive the SAME id, so SyncWrite's `ON CONFLICT (id) DO
 * UPDATE` converges them instead of both inserting and one violating
 * `UNIQUE(diagnosis_entry_id, tooth)` — which would wedge the upload queue.
 */
export function diagnosisToothId(diagnosisEntryId: string, tooth: string): string {
  return `${diagnosisEntryId}:${tooth}`;
}

// ─── Selection ───

/** Ascending FDI order, so `16, 17` never renders as `17, 16`. */
function byFdi(a: string, b: string): number {
  return Number(a) - Number(b);
}

/**
 * Group tooth rows by diagnosis id.
 *
 * Blank teeth are dropped and duplicates collapsed, so a half-synced or
 * legacy row can never render an empty or repeated chip.
 */
export function groupTeethByDiagnosis(rows: readonly DiagnosisToothRow[]): Map<string, string[]> {
  const map = new Map<string, Set<string>>();
  for (const row of rows) {
    const id = row.diagnosis_entry_id;
    const tooth = (row.tooth ?? '').trim();
    if (!id || !tooth) continue;
    const set = map.get(id) ?? new Set<string>();
    set.add(tooth);
    map.set(id, set);
  }

  const out = new Map<string, string[]>();
  for (const [id, set] of map) out.set(id, [...set].sort(byFdi));
  return out;
}

/** Canonical order, de-duplicated, blanks dropped. Never mutates the input. */
function normalizeTeeth(teeth: readonly string[]): string[] {
  return [...new Set(teeth.map((t) => String(t ?? '').trim()).filter(Boolean))].sort(byFdi);
}

/**
 * Dot-joined tooth label: `16`, `16 · 17`, or '' when there is no dental
 * context.
 *
 * '' rather than 'General' — the caller decides how to present the absence, and
 * no fake "General" tooth is ever persisted (PRD §6.4).
 */
export function formatDiagnosisTeeth(teeth: readonly string[]): string {
  return normalizeTeeth(teeth).join(' · ');
}

/** How many teeth a compact row shows before collapsing the rest into `+N`. */
export const DIAGNOSIS_TEETH_VISIBLE = 3;

export interface DiagnosisTeethSummary {
  /** The COMPLETE ordered list — never truncated. Truncation is display-only. */
  all: string[];
  /** The first `maxVisible` teeth. */
  visible: string[];
  /** Teeth collapsed behind `+N`. 0 when everything is visible. */
  hiddenCount: number;
  /** Compact row label: `16 · 17 · 18 +3`, or '' for no dental context. */
  label: string;
  /** Every tooth, for a tooltip / aria-label: `16, 17, 18, 21, 22, 23`. */
  fullLabel: string;
}

/**
 * Summarize a diagnosis's teeth for a compact row (PRD.317 follow-up §4).
 *
 * A diagnosis spanning many teeth would otherwise stretch the tooth column and
 * squeeze the label, so only the first few are shown and the remainder becomes
 * `+N`. This is PRESENTATION ONLY: `all` keeps the full list, and the stored
 * associations in `patient_diagnosis_teeth` are untouched.
 *
 * Returns structured data rather than only a string so the caller can render
 * the visible teeth and the `+N` affordance separately and expose the complete
 * list accessibly.
 */
export function summarizeDiagnosisTeeth(
  teeth: readonly string[],
  maxVisible: number = DIAGNOSIS_TEETH_VISIBLE,
): DiagnosisTeethSummary {
  const all = normalizeTeeth(teeth);
  const ceiling = Math.max(1, maxVisible);
  const visible = all.slice(0, ceiling);
  const hiddenCount = Math.max(0, all.length - visible.length);

  return {
    all,
    visible,
    hiddenCount,
    label: hiddenCount > 0 ? `${visible.join(' · ')} +${hiddenCount}` : visible.join(' · '),
    fullLabel: all.join(', '),
  };
}

// ─── Writes ───

export interface DiagnosisTeethDiff {
  /** Teeth to INSERT. */
  added: string[];
  /** Teeth to DELETE. */
  removed: string[];
}

/**
 * Minimal change set between the stored teeth and the clinician's selection.
 *
 * A diff rather than delete-all-then-insert-all: the latter would queue a
 * DELETE and a PUT for the SAME deterministic id on every save of an unchanged
 * tooth, churning the sync queue and risking the two arriving out of order.
 * Unchanged teeth produce no operation at all.
 */
export function diffDiagnosisTeeth(
  current: readonly string[],
  next: readonly string[],
): DiagnosisTeethDiff {
  const currentSet = new Set(current.map((t) => t.trim()).filter(Boolean));
  const nextSet = new Set(next.map((t) => t.trim()).filter(Boolean));

  return {
    added: [...nextSet].filter((t) => !currentSet.has(t)).sort(byFdi),
    removed: [...currentSet].filter((t) => !nextSet.has(t)).sort(byFdi),
  };
}

// ─── Clinical Findings context (optional) ───

/**
 * Distinct teeth already documented on this encounter's Clinical Findings,
 * offered as convenient tooth context when adding a diagnosis (PRD §10.1).
 *
 * STRICTLY tooth numbers. The finding's narrative and chart condition are
 * deliberately NOT carried across: a carious lesion on 16 is not a diagnosis,
 * and the clinician alone decides what the diagnosis is. Findings that are
 * absent, empty or disabled simply yield [] — Diagnosis never depends on
 * PRD.316 being present (PRD §10.3).
 *
 * `parsedTeethPerFinding` is already-parsed tooth context, so this module
 * stays free of the findings row shape and the two features stay decoupled.
 */
export function suggestedTeethFromFindings(
  parsedTeethPerFinding: readonly (readonly { tooth: string }[])[],
): string[] {
  const set = new Set<string>();
  for (const teeth of parsedTeethPerFinding) {
    for (const t of teeth) {
      const tooth = (t?.tooth ?? '').trim();
      if (tooth) set.add(tooth);
    }
  }
  return [...set].sort(byFdi);
}

/**
 * Merge tooth-context suggestions from several sources into one canonical list.
 *
 * Sources are the patient's CURRENT dental context — teeth charted on this
 * encounter's Part Chart, and teeth already documented on its Clinical Findings
 * (PRD §10.2, §11). Both are conveniences for selecting a tooth.
 *
 * TEETH ONLY, deliberately. A chart condition of `caries` on 16 must never
 * become a suggested diagnosis of "Dental caries" (PRD §11) — the clinician
 * always authors the diagnosis. Nothing here carries a condition code, a
 * finding narrative or any diagnostic wording.
 *
 * Suggestions are NOT filtered against teeth already diagnosed: one tooth may
 * legitimately carry several diagnoses in an encounter (PRD §6.3).
 */
export function mergeSuggestedTeeth(...sources: readonly (readonly string[])[]): string[] {
  const set = new Set<string>();
  for (const source of sources) {
    for (const tooth of source) {
      const t = String(tooth ?? '').trim();
      if (t) set.add(t);
    }
  }
  return [...set].sort(byFdi);
}
